import { useCallback, useRef, useState } from 'react';

export type CallStatus = 'active' | 'hold' | 'transfer';

export interface CallStateController {
  getPublisher: () => any;
  isCameraOn: () => boolean;
  subscribe: (stream: any) => any;
  unsubscribe: (subscriber: any) => void;
  setDisplayedRemoteStream: (stream: any | null) => void;
  setSipAudioAudible: (audible: boolean) => void;
}

const CONSULT_END_GRACE_MS = 5000;

export function useCallState(controller: CallStateController) {
  const [callStatus, setCallStatus] = useState<CallStatus>('active');

  const ctrlRef = useRef(controller);
  ctrlRef.current = controller;

  const isTransferringRef = useRef(false);
  const transferConfirmedRef = useRef(false);
  const preTransferAgentStreamRef = useRef<any>(null);
  const pendingConsultStreamRef = useRef<any>(null);
  const pendingConsultSubscriberRef = useRef<any>(null);
  const consultEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prePauseCameraOnRef = useRef<boolean | null>(null);
  const agentStreamRef = useRef<any>(null);
  const agentSubscriberRef = useRef<any>(null);

  const log = (msg: string) => console.log('[VIDEO][call-state] ' + msg);

  const pauseVisitorVideo = useCallback(() => {
    const pub = ctrlRef.current.getPublisher();
    if (!pub) return;
    if (prePauseCameraOnRef.current === null) {
      prePauseCameraOnRef.current = ctrlRef.current.isCameraOn();
    }
    pub.publishVideo(false);
  }, []);

  const resumeVisitorVideo = useCallback(() => {
    const pub = ctrlRef.current.getPublisher();
    if (pub && prePauseCameraOnRef.current !== false) {
      pub.publishVideo(true);
    }
    prePauseCameraOnRef.current = null;
  }, []);

  const refreshSubscriber = useCallback((sub: any) => {
    if (!sub || typeof sub.subscribeToVideo !== 'function') return;
    try {
      sub.subscribeToVideo(false);
      setTimeout(() => {
        try {
          sub.subscribeToVideo(true);
        } catch {
          /* noop */
        }
      }, 50);
    } catch {
      /* noop */
    }
  }, []);

  const holdCall = useCallback(() => {
    if (isTransferringRef.current) return;
    log('HOLD — pausing video, showing overlay');
    pauseVisitorVideo();
    setCallStatus('hold');
    ctrlRef.current.setSipAudioAudible(true);
  }, [pauseVisitorVideo]);

  const resumeCall = useCallback(() => {
    if (isTransferringRef.current) return;
    log('RESUME — restoring video, hiding overlay');
    resumeVisitorVideo();
    setCallStatus('active');
    ctrlRef.current.setSipAudioAudible(false);
  }, [resumeVisitorVideo]);

  const startTransfer = useCallback(() => {
    if (isTransferringRef.current) return;
    log('TRANSFER — waiting for new specialist, keeping session alive');
    isTransferringRef.current = true;
    preTransferAgentStreamRef.current = agentStreamRef.current;
    pauseVisitorVideo();
    setCallStatus('transfer');
    ctrlRef.current.setSipAudioAudible(true);
  }, [pauseVisitorVideo]);

  const completeTransfer = useCallback(() => {
    if (!isTransferringRef.current) return;
    log('TRANSFER complete — new specialist connected');
    isTransferringRef.current = false;
    transferConfirmedRef.current = false;
    preTransferAgentStreamRef.current = null;
    if (consultEndTimerRef.current) {
      clearTimeout(consultEndTimerRef.current);
      consultEndTimerRef.current = null;
    }
    resumeVisitorVideo();
    setCallStatus('active');
    ctrlRef.current.setSipAudioAudible(false);
  }, [resumeVisitorVideo]);

  const discardPendingConsultStream = useCallback(() => {
    const sub = pendingConsultSubscriberRef.current;
    if (sub) {
      try {
        ctrlRef.current.unsubscribe(sub);
      } catch {
        /* noop */
      }
    }
    pendingConsultStreamRef.current = null;
    pendingConsultSubscriberRef.current = null;
  }, []);

  const promotePendingConsultStream = useCallback(() => {
    const newStream = pendingConsultStreamRef.current;
    const newSub = pendingConsultSubscriberRef.current;
    if (!newStream || !newSub) return;
    log('promoting hidden consult stream to active agent');
    pendingConsultStreamRef.current = null;
    pendingConsultSubscriberRef.current = null;

    try {
      newSub.subscribeToVideo(true);
      newSub.subscribeToAudio(true);
    } catch {
      /* noop */
    }

    const prevSub = agentSubscriberRef.current;
    agentStreamRef.current = newStream;
    agentSubscriberRef.current = newSub;
    ctrlRef.current.setDisplayedRemoteStream(newStream);

    if (prevSub && prevSub !== newSub) {
      try {
        ctrlRef.current.unsubscribe(prevSub);
      } catch {
        /* noop */
      }
    }
    refreshSubscriber(newSub);
  }, [refreshSubscriber]);

  const endConsult = useCallback(() => {
    if (!isTransferringRef.current) return;
    if (transferConfirmedRef.current) {
      log('CONSULT ended but transfer confirmed — staying in transfer');
      return;
    }
    if (consultEndTimerRef.current) clearTimeout(consultEndTimerRef.current);
    log('CONSULT ended (unconfirmed) — waiting ' + CONSULT_END_GRACE_MS + 'ms for a possible transfer');
    consultEndTimerRef.current = setTimeout(() => {
      consultEndTimerRef.current = null;
      if (!isTransferringRef.current || transferConfirmedRef.current) return;
      log('CONSULT cancelled — returning to the same agent');
      isTransferringRef.current = false;
      preTransferAgentStreamRef.current = null;
      discardPendingConsultStream();
      resumeVisitorVideo();
      setCallStatus('active');
      ctrlRef.current.setSipAudioAudible(false);
    }, CONSULT_END_GRACE_MS);
  }, [discardPendingConsultStream, resumeVisitorVideo]);

  const handleCallStateSignal = useCallback(
    (event: any) => {
      let payload: any;
      try {
        payload = typeof event?.data === 'string' ? JSON.parse(event.data) : event?.data;
      } catch {
        return;
      }
      if (!payload || !payload.event) return;
      log('signal ' + JSON.stringify(payload));

      switch (payload.event) {
        case 'hold':
          payload.active ? holdCall() : resumeCall();
          break;

        case 'consult':
          payload.active ? startTransfer() : endConsult();
          break;
        case 'transfer':
          if (payload.active) {
            if (consultEndTimerRef.current) {
              clearTimeout(consultEndTimerRef.current);
              consultEndTimerRef.current = null;
            }
            transferConfirmedRef.current = true;
            if (!isTransferringRef.current) startTransfer();
            if (pendingConsultStreamRef.current) {
              promotePendingConsultStream();
              completeTransfer();
            } else if (agentStreamRef.current !== preTransferAgentStreamRef.current) {
              completeTransfer();
            }
          }
          break;
        default:
          break;
      }
    },
    [holdCall, resumeCall, startTransfer, endConsult, promotePendingConsultStream, completeTransfer],
  );

  const handleStreamCreated = useCallback(
    (stream: any): boolean => {
      if (isTransferringRef.current && !transferConfirmedRef.current) {
        log('new stream during unconfirmed consult — hiding it, keeping current agent');
        const sub = ctrlRef.current.subscribe(stream);
        try {
          sub?.subscribeToVideo(false);
          sub?.subscribeToAudio(false);
        } catch {
          /* noop */
        }
        if (pendingConsultSubscriberRef.current && pendingConsultStreamRef.current !== stream) {
          try {
            ctrlRef.current.unsubscribe(pendingConsultSubscriberRef.current);
          } catch {
            /* noop */
          }
        }
        pendingConsultStreamRef.current = stream;
        pendingConsultSubscriberRef.current = sub;
        return true;
      }

      const sub = ctrlRef.current.subscribe(stream);
      agentStreamRef.current = stream;
      agentSubscriberRef.current = sub;
      ctrlRef.current.setDisplayedRemoteStream(stream);

      if (isTransferringRef.current && transferConfirmedRef.current) {
        completeTransfer();
        refreshSubscriber(sub);
      }
      return true;
    },
    [completeTransfer, refreshSubscriber],
  );

  const handleStreamDestroyed = useCallback((stream: any): boolean => {
    if (isTransferringRef.current) {
      log('streamDestroyed during transfer — NOT tearing down');
      if (agentStreamRef.current === stream) {
        agentStreamRef.current = null;
        agentSubscriberRef.current = null;
      }
      return true;
    }

    if (agentStreamRef.current === stream) {
      agentStreamRef.current = null;
      agentSubscriberRef.current = null;
    }
    ctrlRef.current.setDisplayedRemoteStream(null);
    return false;
  }, []);

  const reset = useCallback(() => {
    isTransferringRef.current = false;
    transferConfirmedRef.current = false;
    preTransferAgentStreamRef.current = null;
    pendingConsultStreamRef.current = null;
    pendingConsultSubscriberRef.current = null;
    prePauseCameraOnRef.current = null;
    agentStreamRef.current = null;
    agentSubscriberRef.current = null;
    if (consultEndTimerRef.current) {
      clearTimeout(consultEndTimerRef.current);
      consultEndTimerRef.current = null;
    }
    setCallStatus('active');
  }, []);

  return {
    callStatus,
    handleCallStateSignal,
    handleStreamCreated,
    handleStreamDestroyed,
    reset,
  };
}
